import { useEffect } from "react";

import type { FirstPassReadingView } from "./first-pass.js";
import type { AnalysisChainView, ConsoleUnderwritingView } from "./underwriting-panel.js";

/**
 * `AA-4a`: how often the screen reads again while the analysis moves. `AA-2b` M9 read the reading every 5 s and the panel read
 * the console run every 3 s, two timers for one chain; the one poller takes the faster, so the bar follows the run.
 */
export const ANALYSIS_POLL_MS = 3_000;

/** What one round of reading brought back: `undefined` for a part that was not asked or whose read failed (the screen keeps what it shows). */
export interface AnalysisSnapshot {
  readonly reading: FirstPassReadingView | null | undefined;
  readonly analysis: AnalysisChainView | null | undefined;
  readonly underwriting: ConsoleUnderwritingView | null | undefined;
}

/**
 * `AA-2b` M9 (`AA-F3`), merged by `AA-4a`: **ONE poller for the whole chain.** While anything moves (the reading under way, the
 * console run in flight, the chain at its reading or its analysis, the filter and the banks included) the reading, the console
 * run and the chain's state are fetched again together every {@link ANALYSIS_POLL_MS}; it stops the moment nothing moves (the
 * flag the caller hands in changes), when the dossier changes, or on unmount. A part whose read fails is tried again at the next
 * tick and the page keeps what it shows.
 */
export function useAnalysisPolling(options: {
  readonly dossierId: string;
  readonly moving: boolean;
  readonly readFirstPass: ((id: string) => Promise<FirstPassReadingView | null>) | undefined;
  readonly readAnalysis: ((id: string) => Promise<AnalysisChainView | null>) | undefined;
  readonly readUnderwriting: ((id: string) => Promise<ConsoleUnderwritingView | null>) | undefined;
  readonly onRead: (snapshot: AnalysisSnapshot) => void;
}): void {
  const { dossierId, moving, readFirstPass, readAnalysis, readUnderwriting, onRead } = options;
  useEffect(() => {
    if (!moving) return;
    const watch: { live: boolean } = { live: true };
    const timer = setInterval(() => {
      void (async () => {
        const settled = async <T>(read: ((id: string) => Promise<T>) | undefined): Promise<T | undefined> => {
          if (read === undefined) return undefined;
          try {
            return await read(dossierId);
          } catch {
            return undefined;
          }
        };
        const [reading, analysis, underwriting] = await Promise.all([
          settled(readFirstPass),
          settled(readAnalysis),
          settled(readUnderwriting),
        ]);
        if (watch.live) onRead({ reading, analysis, underwriting });
      })();
    }, ANALYSIS_POLL_MS);
    return () => {
      watch.live = false;
      clearInterval(timer);
    };
    // `onRead` is the caller's setter wrapper, new at each render; the effect must not restart on it.
  }, [dossierId, moving, readFirstPass, readAnalysis, readUnderwriting]);
}
